import { request, postFormData, axiosInstance } from "../helper/axios_helper";

const BASE = "/api/auth/gateway";

// ─── Systems & templates ──────────────────────────────────────
export const getGatewaySystems    = (email)      => request("GET", `${BASE}/systems?userEmail=${encodeURIComponent(email)}`);
export const getAllGatewaySystems  = ()           => request("GET", `${BASE}/systems/all`);
export const getGatewayTemplates  = (systemId)   => request("GET", `${BASE}/templates?systemId=${systemId}`);
export const getGatewayFields     = (templateId) => request("GET", `${BASE}/templates/${templateId}/fields`);
export const getFormatTypes       = ()           => request("GET", `${BASE}/admin/format-types`);

// ─── Spec upload ─────────────────────────────────────────────
export const uploadSpec = (file, systemId, userEmail) => {
  const fd = new FormData();
  fd.append("file", file);
  return postFormData(
    `${BASE}/templates/upload?systemId=${systemId}&userEmail=${encodeURIComponent(userEmail)}`,
    fd
  );
};

// ─── Column detection ─────────────────────────────────────────
export const detectColumnsFromFile = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return postFormData(`${BASE}/source/columns`, fd);
};
export const detectColumnsFromDb = (dbName, sql) =>
  request("GET", `${BASE}/source/columns?dbName=${encodeURIComponent(dbName)}&sql=${encodeURIComponent(sql)}`);

// ─── Pre-flight validation ────────────────────────────────────
export const runPreflight = (generateRequest, file) => {
  const fd = new FormData();
  fd.append("request", JSON.stringify(generateRequest));
  if (file) fd.append("file", file);
  return postFormData(`${BASE}/preflight`, fd);
};

// ─── Async generation ─────────────────────────────────────────
export const startGeneration = (generateRequest, file) => {
  const fd = new FormData();
  fd.append("request", JSON.stringify(generateRequest));
  if (file) fd.append("file", file);
  return postFormData(`${BASE}/generate`, fd);
};

// ─── Job status polling ───────────────────────────────────────
export const getGatewayJobStatus = (jobId) =>
  request("GET", `${BASE}/job/${jobId}/status`);

/**
 * Poll until job is COMPLETED or FAILED.
 * onProgress(pct, status) called on each poll.
 */
export const pollGatewayJob = (jobId, onProgress) =>
  new Promise((resolve, reject) => {
    const interval = setInterval(async () => {
      try {
        const res    = await getGatewayJobStatus(jobId);
        const status = res.data.status;
        const pct    = res.data.progressPct || 0;
        if (onProgress) onProgress(pct, status, res.data);
        if (status === "COMPLETED") { clearInterval(interval); resolve(res.data); }
        else if (status === "FAILED") { clearInterval(interval); reject(res.data); }
      } catch (err) {
        console.warn("Gateway poll interrupted:", err.message);
      }
    }, 2000); // poll every 2 seconds
  });

// ─── Download generated file ──────────────────────────────────
export const downloadGatewayFile = async (jobId) => {
  const response = await axiosInstance.get(`${BASE}/job/${jobId}/download`, {
    responseType: "blob",
  });
  return {
    blob:         response.data,
    filename:     response.headers["content-disposition"]?.split("filename=")[1]?.replace(/"/g, "") || "output.dat",
    fileHash:     response.headers["x-file-hash"],
    rowCount:     parseInt(response.headers["x-row-count"] || "0"),
  };
};

// ─── Saved mappings ───────────────────────────────────────────
export const getSavedMappings  = (templateId, email) =>
  request("GET", `${BASE}/mappings?templateId=${templateId}&userEmail=${encodeURIComponent(email)}`);
export const saveMappingConfig = (dto)                =>
  request("POST", `${BASE}/mappings`, dto);

// ─── Code tables ──────────────────────────────────────────────
export const getCodeTables        = (systemId)  =>
  request("GET", `${BASE}/codetables${systemId ? `?systemId=${systemId}` : ""}`);
export const saveCodeTableEntry   = (entry)     => request("POST",   `${BASE}/codetables`, entry);
export const deleteCodeTableEntry = (id, src)   =>
  request("DELETE", `${BASE}/codetables/${encodeURIComponent(id)}/${encodeURIComponent(src)}`);

// ─── SFTP ─────────────────────────────────────────────────────
export const getGatewaySftp = (systemId) =>
  request("GET", `${BASE}/sftp${systemId ? `?systemId=${systemId}` : ""}`);

// ─── History ─────────────────────────────────────────────────
export const getGatewayHistory  = (email) =>
  request("GET", `${BASE}/history?userEmail=${encodeURIComponent(email)}`);

// ─── Admin ───────────────────────────────────────────────────
export const getAllGatewayAccess  = ()    => request("GET",    `${BASE}/admin/access`);
export const grantGatewayAccess  = (dto) => request("POST",   `${BASE}/admin/access`, dto);
export const revokeGatewayAccess = (email, systemId) =>
  request("DELETE", `${BASE}/admin/access?userEmail=${encodeURIComponent(email)}&systemId=${systemId}`);
